import { IVR_DATA, SCHEMA_LINK } from '@/constants';
import { HOSTNAME } from '@/constants/constantsAckoDrive';
import { SEO_URL_TYPE } from '@/constants/enums';
import { IGarageDetails } from '@/types';
import { resolvePath } from '@/utils/resolvePath';

const formatTime = (isoTime?: string): string => {
    return isoTime?.split(':').slice(0, 2).join(':') ?? '';
};

export const generateGarageJsonLd = (
    garageDetails: IGarageDetails[],
    urlType: SEO_URL_TYPE,
    city?: string,
    garageOrBrand?: string
): Record<string, any> | null => {
    if (!garageDetails || garageDetails.length === 0) {
        return null;
    }
    const baseUrl = `${HOSTNAME}${process.env.NEXT_PUBLIC_ENV_PREFIX}`;
    const imageUrl = `${HOSTNAME}${resolvePath('/images/ackodrive/ackodrive-new-logo.svg')}`;
    const telephone = IVR_DATA.SERVICE_ADVISOR_NUMBER;

    const resolveUrl = (): string => {
        switch (urlType) {
            case SEO_URL_TYPE.CITY:
                return `${baseUrl}/${city}`;
            case SEO_URL_TYPE.GARAGE_OR_BRAND:
                return `${baseUrl}/${city}/${garageOrBrand}`;
            case SEO_URL_TYPE.SERVICE_CENTRE:
                return `${baseUrl}`;
        }
    };

    const buildEntry = (garage: IGarageDetails): Record<string, any> => ({
        '@type': 'AutoRepair',
        '@id': `${HOSTNAME}${garage.garage_landing_path}`,
        name: garage.name,
        url: resolveUrl(),
        telephone,
        image: imageUrl,
        address: {
            '@type': 'PostalAddress',
            streetAddress: garage.address,
            addressLocality: garage.location?.locality,
            addressRegion: garage.location?.state,
            postalCode: garage.pincode,
            addressCountry: 'IN',
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: garage.location?.location_coords?.latitude,
            longitude: garage.location?.location_coords?.longitude,
        },
        openingHoursSpecification: [
            {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                ],
                opens: formatTime(
                    garage.working_hours?.PICK_AND_DROP?.working_start_time
                ),
                closes: formatTime(
                    garage.working_hours?.PICK_AND_DROP?.working_end_time
                ),
            },
        ],
        sameAs: [garage.map_link],
        hasMap: garage.map_link,
    });

    if (garageDetails.length > 1) {
        return {
            '@context': SCHEMA_LINK,
            '@graph': garageDetails.map(buildEntry),
        };
    } else {
        return {
            '@context': SCHEMA_LINK,
            ...buildEntry(garageDetails[0]),
        };
    }
};
